import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ChapterResourceList from '../components/ChapterResourceList';
import ResourceTypeFilter, { applyTypeFilter } from '../components/ResourceTypeFilter';
import ResourceViewer from '../components/ResourceViewer';
import Icon from '../components/Icon';
import { ErrorBanner } from '../components/Feedback';
import { api } from '../api/client';

/**
 * Module 9 — a book a student has activated: its chapters, and the resources of the
 * chapter they pick.
 *
 * <p>There is no download control anywhere on this screen, and its absence is not what
 * enforces the rule: the server never signs a download link for a student.
 */
export default function BookPage() {
  const { titleId } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [chapter, setChapter] = useState(null);
  const [resources, setResources] = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .studentChapters(titleId)
      .then((result) => {
        if (cancelled) return;
        setBook(result);
        // Open the first chapter straight away: a list of chapter names with nothing
        // under it is a wasted click on the only screen a student really uses.
        if (result.chapters.length > 0) select(result.chapters[0]);
      })
      .catch((err) => !cancelled && setError(err));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titleId]);

  async function select(summary) {
    setChapter(summary);
    setResources(null);
    // A filter chosen for one chapter means nothing in the next one.
    setTypeFilter(null);
    try {
      setResources((await api.studentResources(summary.chapterId)).resources);
    } catch (err) {
      setError(err);
    }
  }

  return (
    <>
      <div className="page-head">
        <button
          type="button"
          className="btn btn-ghost"
          style={{ marginBottom: 'var(--s0)' }}
          onClick={() => navigate('/library')}
        >
          <Icon name="back" size={16} /> My library
        </button>
        <h1>{book?.titleName ?? 'Book'}</h1>
        {book && (
          <p>
            {/* As stored: a title's class is the string "Class 8", not "8", so the
                prefix made it "Class Class 8". */}
            {[book.subject, book.classLevel].filter(Boolean).join(' · ')}
          </p>
        )}
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      {book === null && !error && <div className="empty">Loading…</div>}

      {book && book.chapters.length === 0 && (
        <section className="card">
          <div className="empty">No chapters have been published for this book yet.</div>
        </section>
      )}

      {book && book.chapters.length > 0 && (
        <div className="repo">
          <section className="card">
            {book.chapters.map((summary) => (
              <button
                type="button"
                key={summary.chapterId}
                className={`chapter-row${chapter?.chapterId === summary.chapterId ? ' active' : ''}`}
                onClick={() => select(summary)}
              >
                <span>
                  <span className="muted small">{summary.sequence}.</span> {summary.name}
                </span>
                <span className="tiny muted">{summary.resourceCount}</span>
              </button>
            ))}
          </section>

          <section className="card">
            {chapter && (
              <div
                className="toolbar"
                style={{ padding: 'var(--s1) var(--s2)', justifyContent: 'space-between' }}
              >
                <b>{chapter.name}</b>
              </div>
            )}
            {resources && resources.length > 0 && (
              <div style={{ padding: '0 var(--s2) var(--s1)' }}>
                <ResourceTypeFilter
                  resources={resources}
                  selected={typeFilter}
                  onChange={setTypeFilter}
                />
              </div>
            )}
            <ChapterResourceList
              resources={applyTypeFilter(resources, typeFilter)}
              loading={chapter !== null && resources === null}
              onOpen={setViewing}
              emptyMessage="Nothing has been published for this chapter yet."
            />
          </section>
        </div>
      )}

      {viewing && (
        <ResourceViewer resource={viewing} role="STUDENT" onClose={() => setViewing(null)} />
      )}
    </>
  );
}
